import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import * as contactpersonActions from './contactperson.actions';
import { Customer, CustomerLocation } from '../../shared/models/shared.model';
import {
  AccessRule,
  Application,
  ApplicationGroup,
  Contactperson,
  ContactpersonModuleState,
  NewRole,
} from '../models/contactperson.model';

export const adapter: EntityAdapter<Contactperson> = createEntityAdapter<Contactperson>();
export const initialState = <ContactpersonModuleState>{
  contactpersons: adapter.getInitialState([]),
  loadingContactpersons: false,
  selectedContactperson: null,
  loadingCustomersforSearchbox: false,
  customersForSearchbox: [],
  customersWithRelations: [],
  customerRelationsLocations: [],
  profileId: 0,
  accessRules: [],
  applicationGroups: [],
  applications: [],
  roles: [],
  searchText: '',
};

const contactpersonReducer = createReducer(
  initialState,
  on(contactpersonActions.getContactpersonsRequest, (state) => ({
    ...state,
    loadingContactpersons: true,
  })),
  on(contactpersonActions.getContactpersonsSuccess, (state, { contactPersons }) => ({
    ...state,
    contactpersons: adapter.setAll(contactPersons, state.contactpersons),
    selectedContactperson: null as Contactperson,
    loadingContactpersons: false,
  })),
  on(contactpersonActions.getContactpersonsError, (state) => ({
    ...state,
    contactpersons: adapter.removeAll(state.contactpersons),
    loadingContactpersons: false,
  })),
  on(contactpersonActions.setSelectedContactperson, (state, { contactperson }) => ({
    ...state,
    selectedContactperson: contactperson,
  })),
  on(contactpersonActions.upsertContactpersonSuccess, (state, { contactperson }) => ({
    ...state,
    selectedContactperson: contactperson,
    contactpersons: adapter.upsertOne(contactperson, state.contactpersons),
  })),
  on(contactpersonActions.clearContactpersons, (state) => ({
    ...state,
    contactpersons: adapter.removeAll(state.contactpersons),
  })),
  on(contactpersonActions.getCustomersforSearchboxRequest, (state) => ({
    ...state,
    loadingCustomersforSearchbox: true,
  })),
  on(contactpersonActions.clearCustomerRelationSearchbox, (state) => ({
    ...state,
    customersForSearchbox: [] as Customer[],
  })),
  on(contactpersonActions.getCustomersforSearchboxSuccess, (state, { customers }) => ({
    ...state,
    customersForSearchbox: customers,
    loadingCustomersforSearchbox: false,
  })),
  on(contactpersonActions.getCustomersforSearchboxError, (state) => ({
    ...state,
    customersForSearchbox: [] as Customer[],
    loadingCustomersforSearchbox: false,
  })),
  on(contactpersonActions.getCustomerRelationsSuccess, (state, { customers }) => ({
    ...state,
    customersWithRelations: customers,
  })),
  on(contactpersonActions.getLocationsForRelationWithCustomerSuccess, (state, { locations }) => ({
    ...state,
    customerRelationsLocations: locations,
  })),
  on(contactpersonActions.setProfileId, (state, { profileId }) => ({
    ...state,
    profileId,
  })),
  on(contactpersonActions.setApplications, (state, { applications }) => ({
    ...state,
    applications: applications
      .concat()
      .sort((a, b) => (a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0)),
  })),
  on(contactpersonActions.setApplicationGroups, (state, { applicationGroups }) => ({
    ...state,
    applicationGroups: applicationGroups
      .concat()
      .sort((a, b) => (a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0)),
  })),
  on(contactpersonActions.setRoles, (state, { roles }) => ({
    ...state,
    roles: roles
      .concat()
      .sort((a, b) => (a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0)),
  })),
  on(contactpersonActions.setAccessRules, (state, { accessRules }) => ({
    ...state,
    accessRules,
  })),
  on(contactpersonActions.setUseraccess, (state, { applicationKey, roleKey }) => ({
    ...state,
    accessRules: [...state.accessRules, { applicationKey, roleKey }],
  })),
  on(contactpersonActions.setGroupUseraccess, (state, { groupId }) => ({
    ...state,
    accessRules: [
      ...state.accessRules,
      ...state.applications
        .filter((application) => application.applicationGroupId === groupId)
        .map((application) => ({
          applicationKey: application.key,
          roleKey: application.defaultRoleKey,
        })),
    ],
  })),
  on(contactpersonActions.deleteGroupUseraccessSuccess, (state, { groupId }) => ({
    ...state,
    accessRules: [
      ...state.accessRules.filter(
        (accessRule) =>
          state.applications
            .filter((application) => application.applicationGroupId === groupId)
            .map((application) => application.key)
            .indexOf(accessRule.applicationKey) === -1,
      ),
    ],
  })),
  on(contactpersonActions.clearSelectedContactperson, (state) => ({
    ...state,
    applicationGroups: [] as ApplicationGroup[],
    accessRules: [] as AccessRule[],
    applications: [] as Application[],
    profileId: 0,
    roles: [] as NewRole[],
    selectedContactperson: null as Contactperson,
    customersForSearchbox: [] as Customer[],
    customersWithRelations: [] as Customer[],
    customerRelationsLocations: [] as CustomerLocation[],
  })),
  on(contactpersonActions.searchContactpersons, (state, { searchtext }) => ({
    ...state,
    searchText: searchtext,
  })),
);

export function reducer(state: ContactpersonModuleState | undefined, action: Action) {
  return contactpersonReducer(state, action);
}
